import { HostListener, Injectable, OnInit } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { StorageService } from '../storage/storage.service';
import { NOTIFICATION_STATE_NAME, UID_NAME, UPDATE_APP_TIME } from 'src/app/app.config';
import { ApiService } from '../api/api.service';
import { Observable } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { SwUpdate, VersionEvent } from '@angular/service-worker';

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  deferredPrompt: any; //used to show the install prompt
  private static version: string | undefined = undefined; //pwa version

  constructor(private swUpdate: SwUpdate, private translocoService: TranslocoService, private storageService: StorageService, private deviceService: DeviceDetectorService, private apiService: ApiService) {
    if (typeof window != 'undefined') {
      window.addEventListener('beforeinstallprompt', (e: any) => {
        // Prevent Chrome 67 and earlier from automatically showing the prompt
        e.preventDefault();
        // Stash the event so it can be triggered later.
        this.deferredPrompt = e;
      });
    }
  }

  static getVersion(): string | undefined {
    return PwaService.version;
  }

  /**
   * Allows the user to get network status (online/offline)
   * @returns an Observable with the network status (true => online, false => offline)
   */
  networkStatus(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (typeof window != 'undefined') {
        window.addEventListener('online', () => observer.next(true));
        window.addEventListener('offline', () => observer.next(false));
      } else {
        observer.error('window is undefined');
      }
    });
  }

  checkForUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
        console.log(event.type);
        switch (event.type) {
          case 'NO_NEW_VERSION_DETECTED':
            PwaService.version = (<any>event.version.appData)?.version; //update current version
            break;
          case "VERSION_DETECTED":
            console.log("Version detected", event.version.appData);
            document.location.reload();
            break;
          case "VERSION_INSTALLATION_FAILED":
            console.error("Version installation failed", event.error);
            break;
          case "VERSION_READY":
            console.log("Version ready", "latestVersion: " + event.latestVersion, "currentVersion: " + event.currentVersion);
            break;
        }
      });

      setTimeout(() => {
        this.swUpdate.checkForUpdate();
        setInterval(() => this.swUpdate.checkForUpdate(), UPDATE_APP_TIME); // check every 5 minutes
      }, 2000);
    }
  }

  /**
   * Prompt the user to install the app
   */
  install(): Observable<string> {
    return new Observable<string>((observer) => {
      this.translocoService.selectTranslateObject('download_app_modal').subscribe((messages) => {
        if (!this.isInstalled() && this.deferredPrompt && this.deviceService.os == 'Android') {
          // hide our user interface that shows our A2HS button
          // Show the prompt
          this.deferredPrompt.prompt();
          // Wait for the user to respond to the prompt
          this.deferredPrompt.userChoice.then((choiceResult: any) => {
            if (choiceResult.outcome === 'accepted') {
              observer.next(messages.installing_message);
              window.addEventListener('appinstalled', (e: any) => {
                observer.next(messages.wait_for_the_installation);
                setTimeout(() => observer.complete(), 3000);
              }, { once: true });
            } else {
              observer.error(messages.installation_blocked);
            }
            this.deferredPrompt = null;
          });
        } else
          observer.error(messages.installation_failed);
      });
    });
  }

  /**
   * Check if the application is installed on device
   * @returns boolean true if the app is installed successfully
   */
  isInstalled(): boolean {
    if (typeof window === 'undefined')
      return false;
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  /**
   * Check if device has already installed the app
   * @returns An observable that emits the link of the app or null
   */
  hasAlreadyInstalledApp(): Observable<string | null> {
    return new Observable<string | null>((observer) => {
      if ('getInstalledRelatedApps' in window.navigator) {
        const installedApps = (window.navigator as any).getInstalledRelatedApps();
        console.log(installedApps);
        if (installedApps) {
          installedApps.then((apps: any[]) => {
            // get the base url of the app removing the manifest path
            observer.next(apps[0]?.url?.replace('manifest.webmanifest', '') || null);
            observer.complete();
          }).catch((error: any) => {
            console.error(error);
            observer.next(null);
            observer.complete();
          });
        } else {
          observer.next(null);
          observer.complete();
        }
      } else {
        observer.next(null);
        observer.complete();
      }
    });
  }

  /**
   * Get the current user UID
   * @param pushNotification {boolean} true if the UID is used for push notifications
   * @returns an Observable with the user UID
   */
  getUserUID(pushNotification: boolean = false): Observable<string> {
    return new Observable<string>((observer) => {
      if (!pushNotification && this.storageService.getValue(UID_NAME)) //insight UID
        observer.next(this.storageService.getValue(UID_NAME));
      else if (pushNotification && this.storageService.getValue(NOTIFICATION_STATE_NAME)?.uid) //push notification UID
        observer.next(this.storageService.getValue(NOTIFICATION_STATE_NAME).uid);
      else { //UID non found, get it from the server
        this.apiService.getUserUID().subscribe({
          next: (data) => {
            if (!pushNotification) //save the UID for insight
              this.storageService.saveValue(UID_NAME, data.uid);
            else { //save the UID for push notification, update local storage value
              this.storageService.updateValue(NOTIFICATION_STATE_NAME, {uid: data.uid});
            }
            observer.next(data.uid);
          },
          error: (err) => observer.error(err)
        });
      }
    });
  }
}
